import { defineStore } from 'pinia';
import { REGISTER_REWARD_GUEST_VIEW_SESSION_KEY } from '@/constants/register-reward';
import { clearRegisterRewardGuestSnooze } from '@/utils/register-reward-guest-snooze';
import { trackAnalyticsLog } from '@/utils/analytics-track';
import { shouldSkipGuestPromptForRoute } from '@/utils/quiet-shell-route.js';
import { useTrialFundStore } from '@/stores/trialFund.js';

/** @typedef {'closed' | 'modal'} RegisterRewardGuestView */

function clearGuestViewSession() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(REGISTER_REWARD_GUEST_VIEW_SESSION_KEY);
}

/**
 * 未登录「注册即送 $10」引导窗。
 * 旧已登录「恭喜你成功领取 610 DScoin」窗已下线，登录后改走福利盘 tryOpenLoggedPrompt。
 */
export const useRegisterRewardStore = defineStore('registerReward', {
  state: () => ({
    /** 未登录：closed | modal */
    guestView: /** @type {RegisterRewardGuestView} */ ('closed'),
  }),

  getters: {
    guestModalOpen: (state) => state.guestView === 'modal',
  },

  actions: {
    resetOnLogout() {
      this.resetGuestPrompt();
      clearRegisterRewardGuestSnooze();
    },

    resetGuestPrompt() {
      this.guestView = 'closed';
      clearGuestViewSession();
    },

    tryOpenGuestPrompt(options = {}) {
      if (typeof window === 'undefined') return;
      if (useTrialFundStore().guideBlocksOtherPrompts) return;

      const routePath = String(options.routePath ?? window.location.pathname ?? '');
      if (
        shouldSkipGuestPromptForRoute({
          routePath,
          fullscreenRoute: options.fullscreenRoute,
          quietShell: options.quietShell,
        })
      ) {
        return;
      }

      // 清掉历史 mini session，避免旧页签残留
      clearGuestViewSession();

      this.guestView = 'modal';
      trackAnalyticsLog({
        key: 'welfare.expose',
        label: '福利曝光',
        value: { kind: 'register_guest' },
      });
    },

    /** 关闭未登录引导 */
    minimizeGuestPrompt() {
      if (this.guestView === 'modal') {
        trackAnalyticsLog({
          key: 'welfare.close',
          label: '福利关闭',
          value: {
            kind: 'register_guest',
            reason: 'dismiss',
          },
        });
      }
      this.resetGuestPrompt();
    },

    closeGuestPromptForLogin() {
      if (this.guestView === 'modal') {
        trackAnalyticsLog({
          key: 'welfare.close',
          label: '福利关闭',
          value: {
            kind: 'register_guest',
            reason: 'login',
          },
        });
      }
      this.resetGuestPrompt();
    },
  },
});
